import { APIClass, graphqlOperation } from "aws-amplify";
import { CustomListBondsQuery } from "./CustomListBondsQuery";
import Decimal from "decimal.js";
import { UserData, UserAuthDataBalanceAccount } from "./UserData";
import {
  asAmount,
  asBalanceAccountId,
  asCorporateBondId,
  asUserId,
  Currency,
  UserId,
} from "@goodfill/domain-properties";
import { createFiniteDecimalFromAnyString } from "@goodfill/decimal";
import { accountApiToModel } from "@goodfill/models-typescript";

export interface CustomGetAuthDataTransaction {
  balance: string;
  disponibleBalance: string;
  order: number;
}

interface CustomAccountBalanceAccount {
  id: string;
  currency: Currency | null;
  bondId: string | null;
  transactions: {
    items: CustomGetAuthDataTransaction[];
  };
}

interface CustomAccount {
  balanceAccounts: {
    items: CustomAccountBalanceAccount[];
  };
}

interface CustomGetAuthData {
  getUser?: {
    accounts: {
      items: [
        {
          accountId: string;
          account: CustomAccount;
        },
      ];
    };
    id: string;
    favoriteBonds: string[];
  };
}

export function convertApiCustomAccountBalanceAccountToModel(
  apiBalanceAccount: CustomAccountBalanceAccount,
): UserAuthDataBalanceAccount {
  const balance = apiBalanceAccount.transactions.items.reduce(
    (topItem: CustomGetAuthDataTransaction | null, item: CustomGetAuthDataTransaction) => {
      return topItem === null || item.order > topItem.order ? item : topItem;
    },
    null,
  );

  return {
    id: asBalanceAccountId(apiBalanceAccount.id),
    balance: asAmount(
      balance !== null ? createFiniteDecimalFromAnyString(balance.balance, "balance.balance") : new Decimal(0),
    ),
    disponibleBalance: asAmount(
      balance !== null
        ? createFiniteDecimalFromAnyString(balance.disponibleBalance, "balance.disponibleBalance")
        : new Decimal(0),
    ),
    currency: apiBalanceAccount.currency,
    bondId: apiBalanceAccount.bondId !== null ? asCorporateBondId(apiBalanceAccount.bondId) : null,
    transactions: apiBalanceAccount.transactions.items,
  };
}

export function convertCustomGetAuthApiToModel(getUser: CustomGetAuthData["getUser"]): UserData | null {
  if (getUser == null) {
    return null;
  }

  const apiAccount = getUser.accounts.items[0];

  return new UserData(
    asUserId(getUser.id),
    accountApiToModel({
      id: apiAccount.accountId,
      __typename: "Account",
      owners: [],
      createdAt: "",
      updatedAt: "",
    }),
    apiAccount.account.balanceAccounts.items.map(convertApiCustomAccountBalanceAccountToModel),
    getUser.favoriteBonds.map(asCorporateBondId),
  );
}

export async function doCustomGetUserData(
  graphqlApi: Pick<APIClass, "graphql">,
  userId: UserId,
): Promise<UserData | null> {
  const operation = graphqlOperation(CustomListBondsQuery, { id: `${userId}` });
  const result = (await graphqlApi.graphql(operation)) as {
    data?: CustomGetAuthData;
  };

  if (result.data === undefined) {
    throw new Error("result.data in doCustomGetAuth() must not be undefined");
  }

  const getUser = result.data.getUser;

  return convertCustomGetAuthApiToModel(getUser);
}
